/**
 * Public site identity — the code source of truth for production host, email,
 * phone, and legal operator strings used in UI, SEO, mail, and redirects.
 *
 * Docs: docs/ui-ux/SITE_CONTACT.md
 * Cutover ops: deployment/DOMAIN_CUTOVER.md
 *
 * Production identity matches JetHost DNS for kleaners.bg.
 *
 * Change values here, then run `tests/lib/site-identity-drift.test.ts`.
 * Do not hardcode kleaners.* hosts or info@ addresses in app/components/lib.
 */

export const BRAND_NAME = 'Kleaners';

/** Canonical apex host (no protocol, no www). */
export const CANONICAL_HOST = 'kleaners.bg';

/** Canonical origin used in 301 targets and absolute URL examples. */
export const CANONICAL_ORIGIN = `https://${CANONICAL_HOST}`;

/** Public marketing / legal contact mailbox. */
export const PUBLIC_EMAIL = `info@${CANONICAL_HOST}`;

/** Public display host used in legal copy (www CNAME; app 301s to apex). */
export const SITE_WWW = `www.${CANONICAL_HOST}`;

/** cPanel SMTP hostname from the JetHost zone (mail CNAME → apex until split). */
export const SITE_SMTP_HOST = `mail.${CANONICAL_HOST}`;

/** E.164 format for tel: and wa.me URLs */
export const PHONE_E164 = '+359878423002';

/** Display format (with spaces) */
export const PHONE_DISPLAY = '+359 878 423 002';

export const OWNER_NAME = 'Anton Ivanov';

/** Imprint / invoice operator line (EN punctuation). Tracks the canonical host. */
export const LEGAL_OPERATOR_NAME = `${CANONICAL_HOST.charAt(0).toUpperCase()}${CANONICAL_HOST.slice(1)} - ${OWNER_NAME}`;

export const VAT_ID = 'DE440351956';

/**
 * Former public brand token kept in schema alternateName / llms.txt so
 * historical searches still resolve. Not a live host.
 */
export const FORMER_BRAND_NAME = 'Kleaners.de';

/** Legacy apex host kept on Traefik so bookmarks 301 to the canonical .bg domain. */
export const LEGACY_SITE_DOMAIN = 'kleaners.de';

/**
 * Hosts that must 301 to CANONICAL_ORIGIN (path + query preserved).
 * Keep in sync with Coolify/Traefik labels in deployment/coolify-container-labels.md.
 */
export const LEGACY_REDIRECT_HOSTS = [
  LEGACY_SITE_DOMAIN,
  `www.${LEGACY_SITE_DOMAIN}`,
  SITE_WWW,
] as const;

export type LegacyRedirectHost = (typeof LEGACY_REDIRECT_HOSTS)[number];

export interface LegacyHostRedirect {
  source: string;
  has: Array<{ type: 'host'; value: string }>;
  destination: string;
  permanent: boolean;
}

/** Aliases matching the `SITE_*` names used across the app since #227. */
export const SITE_DOMAIN = CANONICAL_HOST;
export const SITE_URL = CANONICAL_ORIGIN;
export const SITE_EMAIL = PUBLIC_EMAIL;
export const SITE_LEGAL_NAME = LEGAL_OPERATOR_NAME;

export function getPublicMailtoHref(): string {
  return `mailto:${PUBLIC_EMAIL}`;
}

export function hostnameWithoutPort(hostHeader: string | null | undefined): string {
  const raw = (hostHeader ?? '').split(',')[0]?.trim() ?? '';
  if (!raw) {
    return '';
  }
  if (raw.startsWith('[')) {
    const end = raw.indexOf(']');
    return (end === -1 ? raw : raw.slice(1, end)).toLowerCase();
  }
  const colon = raw.lastIndexOf(':');
  if (colon > -1 && /^\d+$/.test(raw.slice(colon + 1))) {
    return raw.slice(0, colon).toLowerCase();
  }
  return raw.toLowerCase();
}

export function isLegacyRedirectHost(hostname: string): hostname is LegacyRedirectHost {
  return (LEGACY_REDIRECT_HOSTS as readonly string[]).includes(hostname);
}

/**
 * Absolute 308/301 location for a request whose Host is a former public name.
 * Returns null when the host is already canonical (or unknown).
 */
export function getLegacyHostRedirectUrl(
  hostHeader: string | null | undefined,
  pathname: string,
  search = '',
): string | null {
  const hostname = hostnameWithoutPort(hostHeader);
  if (!isLegacyRedirectHost(hostname)) {
    return null;
  }
  const path = pathname === '/' ? '' : pathname;
  return `${CANONICAL_ORIGIN}${path}${search}`;
}

/** Next.js `redirects()` entries generated from LEGACY_REDIRECT_HOSTS. */
export function buildLegacyHostRedirects(): LegacyHostRedirect[] {
  return LEGACY_REDIRECT_HOSTS.flatMap(host => [
    {
      source: '/',
      has: [{ type: 'host', value: host }],
      destination: `${CANONICAL_ORIGIN}/`,
      permanent: true,
    },
    {
      source: '/:path*',
      has: [{ type: 'host', value: host }],
      destination: `${CANONICAL_ORIGIN}/:path*`,
      permanent: true,
    },
  ]);
}
